import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ConfidenceChip } from "@/features/simulations/ConfidenceChip";
import type { AutoBatchResponse, AutoCandidate } from "@opus-dt/shared-types";
import { IndicatorCompare, SECOND_COLOR, WINNER_COLOR } from "./charts/IndicatorCompare";

interface CompareCandidatesProps {
  batch: AutoBatchResponse;
}

// Comparar base + vencedor + 2º colocado.
// A tabela de doze células virou gráfico: as diferenças que decidem a recomendação são
// de décimos de ponto, e lidas como texto exigiam comparar números de colunas distintas. O que
// NÃO virou gráfico continua em texto (gargalo e confiança são rótulos, não grandezas).
export function CompareCandidates({ batch }: CompareCandidatesProps) {
  const baseline = batch.candidates.find((c) => c.isBaseline) ?? null;
  const ranked = batch.candidates
    .filter((c) => !c.isBaseline && c.status === "COMPLETED")
    .sort((a, b) => (a.rankPosition ?? 99) - (b.rankPosition ?? 99));
  const realWinner = ranked.find((c) => c.isWinner) ?? null;
  // Sem vencedor real (NO_GAIN/parcial/falha): NÃO rotular um candidato como "Vencedor".
  const top = realWinner ?? ranked[0] ?? null;
  const runnerUp = ranked.find((c) => c.simulationId !== top?.simulationId) ?? null;
  if (!top) return null;

  const topTitle = realWinner ? "Vencedor" : "Melhor candidato";
  const rows = [
    { label: "OEE", key: "oee" as const },
    { label: "Disponibilidade", key: "availability" as const },
    { label: "Desempenho", key: "performance" as const },
    { label: "Qualidade", key: "quality" as const },
  ].map(({ label, key }) => ({
    label,
    base: baseline?.[key] ?? null,
    winner: top[key],
    second: runnerUp?.[key] ?? null,
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle>Comparar candidatos</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {!realWinner ? (
          <p className="text-xs text-muted-foreground">
            Nenhum candidato superou a base no objetivo — a marca destacada é o melhor encontrado,
            não uma recomendação.
          </p>
        ) : null}

        <IndicatorCompare rows={rows} winnerLabel={topTitle} secondLabel="2º colocado" />

        <Legend top={top} topTitle={topTitle} runnerUp={runnerUp} baseline={baseline} />

        <p className="rounded-md border border-border-subtle bg-bg-sunken p-2.5 text-[11px] leading-relaxed text-muted-foreground">
          Os eixos <b>não começam em zero</b>: as diferenças são de décimos de ponto. A faixa de
          cada indicador está rotulada nas pontas — a distância entre as marcas é a diferença real,
          não a proporção.
        </p>

        <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 border-t border-border-subtle pt-3 text-[12px]">
          <dt className="text-muted-foreground">Gargalo</dt>
          <dd>{top.bottleneckNames.join(", ") || "—"}</dd>
          <dt className="text-muted-foreground">Confiança</dt>
          <dd className="flex flex-wrap items-center gap-2">
            <ConfidenceChip
              compact
              level={top.displayedConfidenceLevel ?? top.confidenceLevel}
              basis={top.confidenceBasis}
              trackRecordSource={top.trackRecordSource}
            />
            {runnerUp ? (
              <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground">
                2º:{" "}
                <ConfidenceChip
                  compact
                  level={runnerUp.displayedConfidenceLevel ?? runnerUp.confidenceLevel}
                  basis={runnerUp.confidenceBasis}
                  trackRecordSource={runnerUp.trackRecordSource}
                />
              </span>
            ) : null}
          </dd>
        </dl>
      </CardContent>
    </Card>
  );
}

/** Identidade das marcas nunca fica só na cor — a legenda nomeia cada candidato. */
function Legend({
  top,
  topTitle,
  runnerUp,
  baseline,
}: {
  top: AutoCandidate;
  topTitle: string;
  runnerUp: AutoCandidate | null;
  baseline: AutoCandidate | null;
}) {
  return (
    <ul className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
      {baseline ? (
        <li className="inline-flex items-center gap-1.5">
          <span
            className="h-2.5 w-2.5 shrink-0 rounded-full border-2 border-text-secondary"
            aria-hidden
          />
          Base (referência)
        </li>
      ) : null}
      <li className="inline-flex items-center gap-1.5">
        <Swatch color={WINNER_COLOR} />
        <span className="truncate">
          {topTitle} · {top.label ?? "candidato"}
        </span>
      </li>
      {runnerUp ? (
        <li className="inline-flex items-center gap-1.5">
          <Swatch color={SECOND_COLOR} />
          <span className="truncate">2º · {runnerUp.label ?? "candidato"}</span>
        </li>
      ) : null}
    </ul>
  );
}

function Swatch({ color }: { color: string }) {
  return (
    <span
      className="h-2.5 w-2.5 shrink-0 rounded-full bg-[var(--sw)]"
      style={{ "--sw": color } as React.CSSProperties}
      aria-hidden
    />
  );
}
